import { useEffect, useState, type ReactNode } from 'react';

import styles from './ai-loading.module.css';

export interface AiLoadingProps {
  /** 是否处于等待状态；false 时渲染 children（不传则渲染 null） */
  active?: boolean;
  /** 主标题，如「AI 正在生成练习题」 */
  title?: string;
  /** 阶段性提示文案（自动轮播），也可手动指定单条 */
  hint?: string | string[];
  /** 以覆盖层形式铺在父容器上（父容器需 position: relative） */
  overlay?: boolean;
  /** 紧凑模式：横排小动效 + 进度，适合嵌在聊天气泡等小容器里 */
  compact?: boolean;
  /** active 为 false 时渲染的内容 */
  children?: ReactNode;
}

const DEFAULT_STAGES = [
  '正在理解知识点…',
  '正在检索相关错题…',
  '正在组织解题思路…',
  'AI 正在精心打磨内容…',
];

export function AiLoading({
  active = true,
  title = 'AI 正在思考',
  hint,
  overlay = false,
  compact = false,
  children,
}: AiLoadingProps) {
  const [progress, setProgress] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);

  const stages = hint
    ? Array.isArray(hint)
      ? hint
      : [hint]
    : DEFAULT_STAGES;

  useEffect(() => {
    if (!active) return;
    let p = 0;
    setProgress(0);
    // 模拟进度：越接近 90% 走得越慢，真实响应到达后由父组件切 active 收尾
    const timer = window.setInterval(() => {
      const remain = 90 - p;
      const step = Math.max(0.4, remain * 0.05 + Math.random() * 1.4);
      p = Math.min(90, p + step);
      setProgress(p);
    }, 220);
    return () => window.clearInterval(timer);
  }, [active]);

  useEffect(() => {
    if (!active || stages.length <= 1) return;
    const timer = window.setInterval(() => {
      setStageIdx((i) => (i + 1) % stages.length);
    }, 2600);
    return () => window.clearInterval(timer);
  }, [active, stages.length]);

  if (!active) return <>{children ?? null}</>;

  const pct = Math.floor(progress);

  const progressRow = (
    <div className={styles.progressWrap}>
      <div className={styles.progressTrack}>
        <div className={styles.progressBar} style={{ width: `${pct}%` }} />
      </div>
      <div className={styles.progressPct}>{pct}%</div>
    </div>
  );

  const compactScene = (
    <div className={styles.wrapCompact}>
      <div className={styles.miniStage} aria-hidden="true">
        <div className={styles.miniBulb} />
        <div className={styles.miniBook}>
          <div className={styles.miniBookHalf} />
          <div className={styles.miniBookHalf} />
          <div className={styles.miniFlip} />
        </div>
      </div>
      <div className={styles.compactBody}>
        <div className={styles.compactTitle}>{title}</div>
        {progressRow}
      </div>
    </div>
  );

  const scene = (
    <div className={styles.wrap}>
      <div className={styles.stage} aria-hidden="true">
        {/* 灯泡（灵感） */}
        <div className={styles.bulb}>
          <div className={styles.bulbGlass} />
          <div className={styles.bulbBase} />
          <div className={styles.bulbGlow} />
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span
              key={i}
              className={styles.bulbRay}
              style={{ transform: `rotate(${i * 60}deg) translateY(-46px)` }}
            />
          ))}
        </div>

        {/* 翻开的书 */}
        <div className={styles.book}>
          <div className={styles.bookLeft}>
            <span className={styles.bookLine} />
            <span className={styles.bookLine} />
            <span className={styles.bookLine} />
          </div>
          <div className={styles.bookRight}>
            <span className={styles.bookLine} />
            <span className={styles.bookLine} />
            <span className={styles.bookLine} />
          </div>
          <div className={styles.bookFlip} />
        </div>

        {/* 漂浮的数学符号 */}
        <span className={`${styles.symbol} ${styles.symbol1}`}>π</span>
        <span className={`${styles.symbol} ${styles.symbol2}`}>∑</span>
        <span className={`${styles.symbol} ${styles.symbol3}`}>√</span>
        <span className={`${styles.symbol} ${styles.symbol4}`}>∞</span>

        {/* 神经网络节点 */}
        <div className={styles.network}>
          <span className={`${styles.node} ${styles.nodeA}`} />
          <span className={`${styles.node} ${styles.nodeB}`} />
          <span className={`${styles.node} ${styles.nodeC}`} />
          <span className={styles.linkA} />
          <span className={styles.linkB} />
        </div>
      </div>

      <div className={styles.title}>{title}</div>

      {progressRow}

      <div className={styles.hint}>{stages[stageIdx % stages.length]}</div>
    </div>
  );

  const content = compact ? compactScene : scene;

  if (overlay) {
    return <div className={styles.overlay}>{content}</div>;
  }
  return content;
}
